import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiCheck, FiPercent, FiDollarSign, FiInfo } from 'react-icons/fi';

const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;

const Template = () => {
  const [newTemplateName, setNewTemplateName] = useState('');
  const [selectedFactors, setSelectedFactors] = useState([]);
  const [selectedFactorType, setSelectedFactorType] = useState('new');
  const [flatFactors, setFlatFactors] = useState([]);
  const [percentageFactors, setPercentageFactors] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [isConfirmActionOpen, setIsConfirmActionOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const navigate = useNavigate();

  // ============ FETCH TEMPLATES + FACTORS BY TYPE ============
  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/get-all-pricing-templates`);
      const data = await res.json();
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    }
  };

  const fetchFactorsByType = async () => {
    try {
      const [flatRes, pctRes] = await Promise.all([
        fetch(
          `${AQ_PRICING_BASE_URL}/pricing/api/get-factors-by-type?value_type=flat&is_active_only=true`
        ),
        fetch(
          `${AQ_PRICING_BASE_URL}/pricing/api/get-factors-by-type?value_type=percentage&is_active_only=true`
        ),
      ]);

      const flatData = await flatRes.json();
      const pctData = await pctRes.json();

      setFlatFactors(Array.isArray(flatData) ? flatData : []);
      setPercentageFactors(Array.isArray(pctData) ? pctData : []);
    } catch (error) {
      console.error('Error fetching factors by type:', error);
      toast.error('Failed to load factors');
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchFactorsByType();
  }, []);

  // ============ SELECTION LOGIC WITH MUTUAL EXCLUSIVITY ============
  const getSelectedValueTypes = () => {
    const types = new Set();
    selectedFactors.forEach((name) => {
      if (flatFactors.some((f) => f.factorname === name)) types.add('flat');
      if (percentageFactors.some((f) => f.factorname === name)) types.add('percentage');
    });
    return types;
  };

  const selectedValueTypes = getSelectedValueTypes();
  const isFlatDisabled = selectedValueTypes.has('percentage');
  const isPercentageDisabled = selectedValueTypes.has('flat');

  const handleFactorSelection = (factor, valueType) => {
    const name = factor.factorname;
    const isCurrentlySelected = selectedFactors.includes(name);

    if (!isCurrentlySelected) {
      // Trying to add — check exclusivity
      if (valueType === 'flat' && isFlatDisabled) {
        toast.warning(
          'You cannot combine flat and percentage factors. Deselect percentage factors first.'
        );
        return;
      }
      if (valueType === 'percentage' && isPercentageDisabled) {
        toast.warning(
          'You cannot combine flat and percentage factors. Deselect flat factors first.'
        );
        return;
      }
      setSelectedFactors((prev) => [...prev, name]);
    } else {
      // Removing
      setSelectedFactors((prev) => prev.filter((n) => n !== name));
    }
  };

  const handleTemplateChange = (templateName) => {
    setNewTemplateName(templateName);
    const selectedTemplate = templates.find((template) => template.templatename === templateName);
    setSelectedTemplateId(selectedTemplate ? selectedTemplate.templateid : null);
    if (selectedTemplate) {
      setSelectedFactors(selectedTemplate.factornames || []);
    }
  };

  const handleNewTemplateChange = (event) => {
    const selectedValue = event.target.value;
    if (selectedValue === 'new') {
      setNewTemplateName('');
      setSelectedFactors([]);
      setSelectedTemplateId(null);
    }
  };

  // ============ SAVE ============
  const handleSave = async () => {
    if (newTemplateName.trim() === '' || selectedFactors.length === 0) {
      toast.warning('Please provide a template name and select at least one factor.');
      return;
    }

    setIsSaving(true);

    const allFactors = [...flatFactors, ...percentageFactors];
    const selectedFactorIds = selectedFactors
      .map((factorName) => {
        const factor = allFactors.find((f) => f.factorname === factorName);
        return factor ? factor.factorid : null;
      })
      .filter((id) => id !== null && id !== 0);

    if (selectedFactorIds.length === 0) {
      toast.error('No valid factors selected.');
      setIsSaving(false);
      return;
    }

    const payload = {
      templatename: newTemplateName,
      factorids: selectedFactorIds,
      isactive: true,
      createdby: 'user',
      updatedby: 'user',
    };

    try {
      const url = selectedTemplateId
        ? `${AQ_PRICING_BASE_URL}/pricing/api/update-pricing-template/${selectedTemplateId}`
        : `${AQ_PRICING_BASE_URL}/pricing/api/create-pricing-template`;

      const response = await fetch(url, {
        method: selectedTemplateId ? 'PUT' : 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          selectedTemplateId ? 'Template updated successfully!' : 'Template created successfully!'
        );

        setNewTemplateName('');
        setSelectedFactors([]);
        setSelectedTemplateId(null);
        setSelectedFactorType('new');

        await fetchTemplates();
      } else {
        const errorMsg = data.detail?.[0]?.msg || data.message || 'Unknown error';
        toast.error(`Error: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Error saving pricing template:', error);
      toast.error('An error occurred while saving the template.');
    }

    setIsSaving(false);
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      const response = await fetch(
        `${AQ_PRICING_BASE_URL}/pricing/api/delete-pricing-template/${templateId}`,
        {
          method: 'DELETE',
          headers: { accept: 'application/json' },
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success('Template deleted successfully!');
        setTemplates(templates.filter((template) => template.templateid !== templateId));
        setSelectedTemplateId(null);
        setSelectedFactors([]);
        setNewTemplateName('');
        setSelectedFactorType('new');
      } else {
        toast.error(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('An error occurred while deleting the template.');
    }
  };

  const openConfirmAction = (action) => {
    setActionType(action);
    setIsConfirmActionOpen(true);
  };

  const handleCancelEdit = () => {
    setIsConfirmActionOpen(false);
    setActionType('');
  };

  const handleConfirmAction = () => {
    setIsConfirmActionOpen(false);
    if (actionType === 'create' || actionType === 'update') {
      handleSave();
    } else if (actionType === 'delete') {
      handleDeleteTemplate(selectedTemplateId);
    }
    setActionType('');
  };

  // ============ FACTOR CARD RENDERER ============
  const renderFactorCard = (factor, valueType) => {
    const isSelected = selectedFactors.includes(factor.factorname);
    const disabled =
      (!isSelected && valueType === 'flat' && isFlatDisabled) ||
      (!isSelected && valueType === 'percentage' && isPercentageDisabled);

    return (
      <label
        key={factor.factorid}
        className={`relative flex items-start gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
          isSelected
            ? 'border-black bg-black/5 shadow-sm'
            : disabled
              ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
              : 'border-gray-200 bg-white hover:border-gray-400 hover:shadow-sm'
        }`}
      >
        <input
          type="checkbox"
          checked={isSelected}
          disabled={disabled}
          onChange={() => handleFactorSelection(factor, valueType)}
          className="sr-only"
        />

        {/* Custom checkbox */}
        <div
          className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
            isSelected
              ? 'bg-black border-black'
              : disabled
                ? 'bg-gray-100 border-gray-300'
                : 'bg-white border-gray-400'
          }`}
        >
          {isSelected && <FiCheck size={14} className="text-white" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {factor.factorname}
            </span>
            <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
              {valueType === 'flat' ? `₹${factor.default_value}` : `${factor.default_value}%`}
            </span>
          </div>

          {factor.description && (
            <p className="text-xs text-gray-500 line-clamp-1 mb-1">{factor.description}</p>
          )}

          <div className="flex flex-wrap gap-1">
            <span
              className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-medium ${
                factor.factor_type === 'amenity'
                  ? 'bg-blue-100 text-blue-700'
                  : factor.factor_type === 'service'
                    ? 'bg-green-100 text-green-700'
                    : factor.factor_type === 'auto'
                      ? 'bg-purple-100 text-purple-700'
                      : factor.factor_type === 'seasonal'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-700'
              }`}
            >
              {factor.factor_type}
            </span>
            {factor.auto_apply && (
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">
                auto
              </span>
            )}
          </div>
        </div>
      </label>
    );
  };

  // Filter templates for search (kept for future use; not rendered below)
  const filteredTemplates = templates.filter((template) =>
    template.templatename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen flex flex-col">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Header — UNCHANGED */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Pricing Service</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-overview')}
            className="btn"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-template')}
            className="btn text-black font-bold"
          >
            Templates
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/modify-factors')}
            className="btn"
          >
            Factors
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing')}
            className="btn"
          >
            Pricing
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/room-rates')}
            className="btn"
          >
            Room Rates
          </button>
        </div>
      </header>

      <div className="flex justify-center flex-grow">
        <div className="w-full h-auto bg-white p-8 flex flex-col">
          <h1 className="text-xl font-bold text-center text-black">Modify Templates</h1>
          <h1 className="text-sm font-semibold text-center mb-1 text-gray-600">
            Create custom pricing templates to define rates for various room types based on your
            business needs.
          </h1>
          <hr className="border border-gray-300 w-1/3 mx-auto mb-7" />

          {/* Template Type Selection & Template Name */}
          <div className="mb-6 grid grid-cols-3 gap-4">
            <div>
              <label className="block text-base font-medium text-gray-700 mb-1">
                Select Template Type
              </label>
              <select
                value={selectedFactorType}
                onChange={(e) => {
                  setSelectedFactorType(e.target.value);
                  handleNewTemplateChange(e);
                }}
                className="w-full border border-gray-300 rounded px-2 py-2 text-sm bg-white"
              >
                <option value="new">New Template</option>
                <option value="existing">Existing Template</option>
              </select>
            </div>

            <div className="col-span-2">
              {selectedFactorType === 'new' && (
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-1">
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={newTemplateName}
                    placeholder="Enter template name"
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                  />
                </div>
              )}

              {selectedFactorType === 'existing' && (
                <div className="flex flex-col">
                  <label className="text-base font-medium text-gray-700 mb-1">
                    Select Existing Template
                  </label>
                  <select
                    value={newTemplateName}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    className="w-full capitalize border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                  >
                    <option value="">Select Template</option>
                    {templates.map((template) => (
                      <option key={template.templateid} value={template.templatename}>
                        {template.templatename}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Exclusivity info banner */}
          <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <FiInfo className="text-amber-600 mt-0.5 flex-shrink-0" size={16} />
            <p className="text-xs text-amber-800">
              <span className="font-semibold">Note:</span> You can select multiple{' '}
              <span className="font-semibold">percentage</span> factors OR multiple{' '}
              <span className="font-semibold">flat</span> factors — but not both together. Selecting
              one type will disable the other.
            </p>
          </div>

          {/* Selected summary */}
          {selectedFactors.length > 0 && (
            <div className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-1">
                Selected Factors ({selectedFactors.length})
              </p>
              <p className="text-xs text-gray-600">{selectedFactors.join(', ')}</p>
            </div>
          )}

          {/* ============ FACTOR SELECTION — TWO COLUMNS ============ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* PERCENTAGE FACTORS */}
            <div className="border border-gray-200 rounded-xl p-4 bg-gradient-to-b from-purple-50/40 to-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <FiPercent className="text-purple-700" size={16} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Percentage Factors</h3>
                    <p className="text-xs text-gray-500">Multiplicative adjustments on base rate</p>
                  </div>
                </div>
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {percentageFactors.length}
                </span>
              </div>

              {isPercentageDisabled && (
                <div className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Disabled — flat factors are selected
                </div>
              )}

              {percentageFactors.length === 0 ? (
                <p className="text-xs text-gray-400 italic py-6 text-center">
                  No active percentage factors available.
                </p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {percentageFactors.map((f) => renderFactorCard(f, 'percentage'))}
                </div>
              )}
            </div>

            {/* FLAT FACTORS */}
            <div className="border border-gray-200 rounded-xl p-4 bg-gradient-to-b from-blue-50/40 to-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FiDollarSign className="text-blue-700" size={16} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Flat Factors</h3>
                    <p className="text-xs text-gray-500">Fixed ₹ add-ons to base rate</p>
                  </div>
                </div>
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {flatFactors.length}
                </span>
              </div>

              {isFlatDisabled && (
                <div className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Disabled — percentage factors are selected
                </div>
              )}

              {flatFactors.length === 0 ? (
                <p className="text-xs text-gray-400 italic py-6 text-center">
                  No active flat factors available.
                </p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {flatFactors.map((f) => renderFactorCard(f, 'flat'))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => openConfirmAction(selectedTemplateId ? 'update' : 'create')}
              disabled={isSaving}
              className="px-10 py-2 bg-black text-white font-medium rounded hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>

            <button
              className="px-8 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
              onClick={() => {
                setNewTemplateName('');
                setSelectedFactors([]);
                setSelectedTemplateId(null);
                setSelectedFactorType('new');
              }}
            >
              Cancel
            </button>

            {selectedTemplateId && (
              <button
                onClick={() => openConfirmAction('delete')}
                className="px-10 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Popup — unchanged */}
      {isConfirmActionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg border border-gray-300">
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              {actionType === 'create' && 'Create New Template'}
              {actionType === 'update' && 'Update Template'}
              {actionType === 'delete' && 'Delete Template'}
            </h2>

            <div className="mb-8">
              {actionType === 'create' && (
                <div className="space-y-4">
                  <p className="text-gray-700 text-base">
                    You are about to create a new pricing template with the following details:
                  </p>
                  <div className="bg-gray-50 p-4 rounded border border-gray-200">
                    <p className="text-base font-semibold text-gray-800 mb-2">Template Name:</p>
                    <p className="text-lg font-medium text-gray-900 bg-white p-3 rounded border border-gray-300">
                      {newTemplateName}
                    </p>
                    <p className="text-base font-semibold text-gray-800 mt-4 mb-2">
                      Selected Factors:
                    </p>
                    {selectedFactors.length > 0 && (
                      <div className="bg-white p-3 rounded border border-gray-300 mt-2">
                        <p className="text-sm text-gray-700">
                          {selectedFactors.slice(0, 6).join(', ')}
                          {selectedFactors.length > 6 &&
                            ` and ${selectedFactors.length - 6} more...`}
                        </p>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-800 text-sm font-semibold bg-yellow-200 rounded-md p-2">
                    Note: This template will be available for pricing calculations immediately after
                    creation.
                  </p>
                </div>
              )}

              {actionType === 'update' && (
                <div className="space-y-4">
                  <p className="text-gray-700 text-base">You are about to update the template:</p>
                  <div className="bg-gray-50 p-4 rounded border border-gray-200">
                    <p className="text-base font-semibold text-gray-800 mb-2">Template Name:</p>
                    <p className="text-lg font-medium text-gray-900 bg-white p-3 rounded border border-gray-300">
                      {newTemplateName}
                    </p>
                    <p className="text-base font-semibold text-gray-800 mt-4 mb-2">
                      Selected Factors:
                    </p>
                    {selectedFactors.length > 0 && (
                      <div className="bg-white p-3 rounded border border-gray-300 mt-2">
                        <p className="text-sm text-gray-700">
                          {selectedFactors.slice(0, 6).join(', ')}
                          {selectedFactors.length > 6 &&
                            ` and ${selectedFactors.length - 6} more...`}
                        </p>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-800 text-sm font-semibold bg-yellow-200 rounded-md p-2">
                    Note: All pricing calculations using this template will be updated with the new
                    factors.
                  </p>
                </div>
              )}

              {actionType === 'delete' && (
                <div className="space-y-4">
                  <p className="text-gray-700 text-base">You are about to delete the template:</p>
                  <div className="bg-red-50 p-4 rounded border border-red-200">
                    <p className="text-base font-semibold text-red-800 mb-2">Template Name:</p>
                    <p className="text-lg font-medium text-red-900 bg-white p-3 rounded border border-red-300">
                      {newTemplateName}
                    </p>
                    <p className="text-base font-semibold text-red-800 mt-4 mb-2">Warning:</p>
                    <p className="text-base text-red-700 bg-white p-3 rounded border border-red-300">
                      This action cannot be undone. All pricing configurations using this template
                      will be affected.
                    </p>
                  </div>
                  <p className="text-gray-800 text-sm font-semibold bg-yellow-200 rounded-md p-2">
                    Note: This will permanently remove the template from the system. Make sure this
                    template is not currently in use.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={handleCancelEdit}
                className="px-8 py-3 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 transition-colors font-medium text-base"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                className={`px-8 py-3 text-white rounded hover:bg-gray-800 transition-colors font-medium text-base ${
                  actionType === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-black hover:bg-gray-800'
                }`}
              >
                {actionType === 'create' && 'Create Template'}
                {actionType === 'update' && 'Update Template'}
                {actionType === 'delete' && 'Delete Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Template;

// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;

// const Template = () => {
//   const [newTemplateName, setNewTemplateName] = useState('');
//   const [selectedFactors, setSelectedFactors] = useState([]);
//   const [selectedFactorType, setSelectedFactorType] = useState('new');
//   const [availableFactors, setAvailableFactors] = useState([]);
//   const [isSaving, setIsSaving] = useState(false);
//   const [templates, setTemplates] = useState([]);
//   const [selectedTemplateId, setSelectedTemplateId] = useState(null);
//   const [isConfirmActionOpen, setIsConfirmActionOpen] = useState(false);
//   const [actionType, setActionType] = useState('');
//   const [searchTerm, setSearchTerm] = useState('');

//   const navigate = useNavigate();

//   // Fetch templates and factor names from API
//   useEffect(() => {
//     const fetchTemplatesAndFactors = async () => {
//       try {
//         const templatesResponse = await fetch(
//           `${AQ_PRICING_BASE_URL}/pricing/api/get-all-pricing-templates`
//         );
//         const templatesData = await templatesResponse.json();
//         setTemplates(templatesData);

//         const factorsResponse = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/get-factors`);
//         const factorsData = await factorsResponse.json();
//         setAvailableFactors(factorsData);
//       } catch (error) {
//         console.error('Error fetching templates or factors:', error);
//         toast.error('Failed to load templates and factors');
//       }
//     };

//     fetchTemplatesAndFactors();
//   }, []);

//   const handleFactorSelection = (factor) => {
//     setSelectedFactors((prev) =>
//       prev.includes(factor) ? prev.filter((item) => item !== factor) : [...prev, factor]
//     );
//   };

//   const handleTemplateChange = (templateName) => {
//     setNewTemplateName(templateName);
//     const selectedTemplate = templates.find((template) => template.templatename === templateName);
//     setSelectedTemplateId(selectedTemplate ? selectedTemplate.templateid : null);
//     if (selectedTemplate) {
//       setSelectedFactors(selectedTemplate.factornames);
//     }
//   };

//   const handleNewTemplateChange = (event) => {
//     const selectedValue = event.target.value;
//     if (selectedValue === 'new') {
//       setNewTemplateName('');
//       setSelectedFactors([]);
//       setSelectedTemplateId(null);
//     }
//   };

//   const handleSave = async () => {
//     if (newTemplateName.trim() === '' || selectedFactors.length === 0) {
//       toast.warning('Please provide a template name and select at least one factor.');
//       return;
//     }

//     setIsSaving(true);

//     const selectedFactorIds = selectedFactors
//       .map((factorName) => {
//         const factor = availableFactors.find((factor) => factor.factorname === factorName);
//         return factor ? factor.factorid : null;
//       })
//       .filter((id) => id !== null && id !== 0);

//     if (selectedFactorIds.length === 0) {
//       toast.error('No valid factors selected.');
//       setIsSaving(false);
//       return;
//     }

//     const payload = {
//       templatename: newTemplateName,
//       factorids: selectedFactorIds,
//       isactive: true,
//       createdby: 'user',
//       updatedby: 'user',
//     };

//     try {
//       const url = selectedTemplateId
//         ? `${AQ_PRICING_BASE_URL}/pricing/api/update-pricing-template/${selectedTemplateId}`
//         : `${AQ_PRICING_BASE_URL}/pricing/api/create-pricing-template`;

//       const response = await fetch(url, {
//         method: selectedTemplateId ? 'PUT' : 'POST',
//         headers: {
//           Accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(payload),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         if (selectedTemplateId) {
//           toast.success('Template updated successfully!');
//         } else {
//           toast.success('Template created successfully!');
//         }

//         setNewTemplateName('');
//         setSelectedFactors([]);
//         setSelectedTemplateId(null);
//         setSelectedFactorType('new');

//         // Refresh templates list
//         const templatesResponse = await fetch(
//           `${AQ_PRICING_BASE_URL}/pricing/api/get-all-pricing-templates`
//         );
//         const templatesData = await templatesResponse.json();
//         setTemplates(templatesData);
//       } else {
//         const errorMsg = data.detail?.[0]?.msg || data.message || 'Unknown error';
//         toast.error(`Error: ${errorMsg}`);
//       }
//     } catch (error) {
//       console.error('Error saving pricing template:', error);
//       toast.error('An error occurred while saving the template.');
//     }

//     setIsSaving(false);
//   };

//   const handleDeleteTemplate = async (templateId) => {
//     try {
//       const response = await fetch(
//         `${AQ_PRICING_BASE_URL}/pricing/api/delete-pricing-template/${templateId}`,
//         {
//           method: 'DELETE',
//           headers: {
//             accept: 'application/json',
//           },
//         }
//       );

//       const data = await response.json();

//       if (response.ok) {
//         toast.success('Template deleted successfully!');
//         setTemplates(templates.filter((template) => template.templateid !== templateId));
//         setSelectedTemplateId(null);
//         setSelectedFactors([]);
//         setNewTemplateName('');
//         setSelectedFactorType('new');
//       } else {
//         toast.error(`Error: ${data.message}`);
//       }
//     } catch (error) {
//       console.error('Error deleting template:', error);
//       toast.error('An error occurred while deleting the template.');
//     }
//   };

//   const openConfirmAction = (action) => {
//     setActionType(action);
//     setIsConfirmActionOpen(true);
//   };

//   const handleCancelEdit = () => {
//     setIsConfirmActionOpen(false);
//     setActionType('');
//   };

//   const handleConfirmAction = () => {
//     setIsConfirmActionOpen(false);
//     if (actionType === 'create') {
//       handleSave();
//     } else if (actionType === 'update') {
//       handleSave();
//     } else if (actionType === 'delete') {
//       handleDeleteTemplate(selectedTemplateId);
//     }
//     setActionType('');
//   };

//   // Filter templates for search
//   const filteredTemplates = templates.filter((template) =>
//     template.templatename.toLowerCase().includes(searchTerm.toLowerCase())
//   );

//   return (
//     <div className="p-6 bg-gray-50 min-h-screen flex flex-col">
//       {/* Toast Container */}
//       <ToastContainer
//         position="top-right"
//         autoClose={3000}
//         hideProgressBar={false}
//         newestOnTop={false}
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         theme="light"
//       />

//       {/* Header - Keep existing only */}
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">Pricing Service</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-overview')}
//             className="btn"
//           >
//             Overview
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-template')}
//             className="btn text-black font-bold"
//           >
//             Templates
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/modify-factors')}
//             className="btn"
//           >
//             Factors
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing')}
//             className="btn"
//           >
//             Pricing
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/room-rates')}
//             className="btn"
//           >
//             Room Rates
//           </button>
//         </div>
//       </header>

//       <div className="flex justify-center flex-grow">
//         <div className="w-full h-auto bg-white p-8 flex flex-col">
//           <h1 className="text-xl font-bold text-center text-black">Modify Templates</h1>
//           <h1 className="text-sm font-semibold text-center mb-1 text-gray-600">
//             Create custom pricing templates to define rates for various room types based on your
//             business needs.
//           </h1>
//           <hr className="border border-gray-300 w-1/3 mx-auto mb-7" />

//           {/* Template Type Selection & Template Name */}
//           <div className="mb-6 grid grid-cols-3 gap-4">
//             {/* Template Type Selection */}
//             <div>
//               <label className="block text-base font-medium text-gray-700 mb-1">
//                 Select Template Type
//               </label>
//               <select
//                 value={selectedFactorType}
//                 onChange={(e) => {
//                   setSelectedFactorType(e.target.value);
//                   handleNewTemplateChange(e);
//                 }}
//                 className="w-full border border-gray-300 rounded px-2 py-2 text-sm bg-white"
//               >
//                 <option value="new">New Template</option>
//                 <option value="existing">Existing Template</option>
//               </select>
//             </div>

//             {/* Conditional Rendering Based on Template Type */}
//             <div className="col-span-2">
//               {selectedFactorType === 'new' && (
//                 <div>
//                   <label className="block text-base font-medium text-gray-700 mb-1">
//                     Template Name
//                   </label>
//                   <input
//                     type="text"
//                     value={newTemplateName}
//                     placeholder="Enter template name"
//                     onChange={(e) => setNewTemplateName(e.target.value)}
//                     className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
//                   />
//                 </div>
//               )}

//               {selectedFactorType === 'existing' && (
//                 <div className="flex flex-col">
//                   <label className="text-base font-medium text-gray-700 mb-1">
//                     Select Existing Template
//                   </label>
//                   <select
//                     value={newTemplateName}
//                     onChange={(e) => handleTemplateChange(e.target.value)}
//                     className="w-full capitalize border border-gray-300 rounded px-3 py-2 text-sm bg-white"
//                   >
//                     <option value="">Select Template</option>
//                     {templates.map((template) => (
//                       <option key={template.templateid} value={template.templatename}>
//                         {template.templatename}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Select Factors - No scrollbar */}
//           <div className="mb-6">
//             <label className="block text-base font-medium text-gray-700 mb-2">Select Factors</label>
//             <div className="grid grid-cols-3 gap-3">
//               {availableFactors.map((factor) => {
//                 const percentage = parseFloat(factor.defaultpercentage);
//                 const percentageText = `${percentage}%`;

//                 return (
//                   <label key={factor.factorid} className="flex items-center text-gray-700">
//                     <input
//                       type="checkbox"
//                       checked={selectedFactors.includes(factor.factorname)}
//                       onChange={() => handleFactorSelection(factor.factorname)}
//                       className={`h-4 w-4 mr-2 border-gray-300 rounded ${
//                         !factor.isactive ? 'opacity-50 cursor-not-allowed' : ''
//                       }`}
//                       disabled={!factor.isactive}
//                     />
//                     <span className={`text-sm ${!factor.isactive ? 'text-gray-400' : ''}`}>
//                       {factor.factorname} ({percentageText}) {factor.isactive ? '' : '(Inactive)'}
//                     </span>
//                   </label>
//                 );
//               })}
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex justify-end gap-3 pt-6">
//             <button
//               onClick={() => openConfirmAction(selectedTemplateId ? 'update' : 'create')}
//               disabled={isSaving}
//               className="px-10 py-2 bg-black text-white font-medium rounded hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
//             >
//               {isSaving ? 'Saving...' : 'Save'}
//             </button>

//             <button
//               className="px-8 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
//               onClick={() => {
//                 setNewTemplateName('');
//                 setSelectedFactors([]);
//                 setSelectedTemplateId(null);
//                 setSelectedFactorType('new');
//               }}
//             >
//               Cancel
//             </button>

//             {selectedTemplateId && (
//               <button
//                 onClick={() => openConfirmAction('delete')}
//                 className="px-10 py-2 bg-red-500 text-black rounded  transition-colors"
//               >
//                 Delete
//               </button>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Confirmation Popup */}
//       {isConfirmActionOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg border border-gray-300">
//             <h2 className="text-xl font-bold text-gray-800 mb-2">
//               {actionType === 'create' && 'Create New Template'}
//               {actionType === 'update' && 'Update Template'}
//               {actionType === 'delete' && 'Delete Template'}
//             </h2>

//             <div className="mb-8">
//               {actionType === 'create' && (
//                 <div className="space-y-4">
//                   <p className="text-gray-700 text-base">
//                     You are about to create a new pricing template with the following details:
//                   </p>
//                   <div className="bg-gray-50 p-4 rounded border border-gray-200">
//                     <p className="text-base font-semibold text-gray-800 mb-2">Template Name:</p>
//                     <p className="text-lg font-medium text-gray-900 bg-white p-3 rounded border border-gray-300">
//                       {newTemplateName}
//                     </p>

//                     <p className="text-base font-semibold text-gray-800 mt-4 mb-2">
//                       Selected Factors:
//                     </p>
//                     {selectedFactors.length > 0 && (
//                       <div className="bg-white p-3 rounded border border-gray-300 mt-2">
//                         <p className="text-sm text-gray-700">
//                           {selectedFactors.slice(0, 6).join(', ')}
//                           {selectedFactors.length > 6 &&
//                             ` and ${selectedFactors.length - 6} more...`}
//                         </p>
//                       </div>
//                     )}
//                   </div>
//                   <p className="text-gray-800 text-sm font-semibold bg-yellow-200 rounded-md p-2">
//                     Note: This template will be available for pricing calculations immediately after
//                     creation.
//                   </p>
//                 </div>
//               )}

//               {actionType === 'update' && (
//                 <div className="space-y-4">
//                   <p className="text-gray-700 text-base">You are about to update the template:</p>
//                   <div className="bg-gray-50 p-4 rounded border border-gray-200">
//                     <p className="text-base font-semibold text-gray-800 mb-2">Template Name:</p>
//                     <p className="text-lg font-medium text-gray-900 bg-white p-3 rounded border border-gray-300">
//                       {newTemplateName}
//                     </p>

//                     <p className="text-base font-semibold text-gray-800 mt-4 mb-2">
//                       Selected Factors:
//                     </p>
//                     {selectedFactors.length > 0 && (
//                       <div className="bg-white p-3 rounded border border-gray-300 mt-2">
//                         <p className="text-sm text-gray-700">
//                           {selectedFactors.slice(0, 6).join(', ')}
//                           {selectedFactors.length > 6 &&
//                             ` and ${selectedFactors.length - 6} more...`}
//                         </p>
//                       </div>
//                     )}
//                   </div>
//                   <p className="text-gray-800 text-sm font-semibold bg-yellow-200 rounded-md p-2">
//                     Note: All pricing calculations using this template will be updated with the new
//                     factors.
//                   </p>
//                 </div>
//               )}

//               {actionType === 'delete' && (
//                 <div className="space-y-4">
//                   <p className="text-gray-700 text-base">You are about to delete the template:</p>
//                   <div className="bg-red-50 p-4 rounded border border-red-200">
//                     <p className="text-base font-semibold text-red-800 mb-2">Template Name:</p>
//                     <p className="text-lg font-medium text-red-900 bg-white p-3 rounded border border-red-300">
//                       {newTemplateName}
//                     </p>

//                     <p className="text-base font-semibold text-red-800 mt-4 mb-2">Warning:</p>
//                     <p className="text-base text-red-700 bg-white p-3 rounded border border-red-300">
//                       This action cannot be undone. All pricing configurations using this template
//                       will be affected.
//                     </p>
//                   </div>
//                   <p className="text-gray-800 text-sm font-semibold bg-yellow-200 rounded-md p-2">
//                     Note: This will permanently remove the template from the system. Make sure this
//                     template is not currently in use.
//                   </p>
//                 </div>
//               )}
//             </div>

//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={handleCancelEdit}
//                 className="px-8 py-3 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 transition-colors font-medium text-base"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleConfirmAction}
//                 className={`px-8 py-3 text-white rounded hover:bg-gray-800 transition-colors font-medium text-base ${
//                   actionType === 'delete'
//                     ? 'bg-red-600 hover:bg-red-700'
//                     : 'bg-black hover:bg-gray-800'
//                 }`}
//               >
//                 {actionType === 'create' && 'Create Template'}
//                 {actionType === 'update' && 'Update Template'}
//                 {actionType === 'delete' && 'Delete Template'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Template;
